/* eslint-disable @typescript-eslint/member-ordering */
import axios, { AxiosInstance } from 'axios';
import { addPending, removePending } from './axiosReqCancel';
import apiUrls from 'apis/apiUrls';
import { storageService } from 'services';
type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

let tokenPromise: any = null;
const refreshTokenApiUrl = apiUrls.refreshToken;
const loginApiUrl = apiUrls.login;

export interface IInstanceConfig {
  baseURL: string;
  retries: number;
  retriesDelay?: number;
  headers?: any;
  timeout?: number;
}

export interface IRequest {
  url: string;
  config?: any | undefined;
  data?: any;
}

export interface IAxiosClientInterface {
  instance: AxiosInstance;
  setHeader(key: string, value: string | undefined): void;
  makePostRequest(requestObject: IRequest): any;
  makePutRequest(requestObject: IRequest): any;
  makeGetRequest(requestObject: IRequest): any;
}

// function delay(ms: number) {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// }

const byPassUrl = new Set([
  'byPassUrl',
  apiUrls.getAllCity(),
  apiUrls.getAllZones(),
  apiUrls.getStores(),
  apiUrls.getCityByZone(),
]);

export default class AxiosClient implements IAxiosClientInterface {
  instance: AxiosInstance;
  retries: number;
  retriesDelay: number;

  constructor({
    baseURL,
    headers,
    retries,
    retriesDelay,
    // timeout = 10000,
  }: IInstanceConfig) {
    this.instance = axios.create({ baseURL, headers });
    this.retriesDelay = retriesDelay || 2;
    this.retries = retries || 2;

    this.instance.interceptors.request.use((interceptor: any) => {
      if (['get', 'post'].includes(interceptor.method) && !byPassUrl.has(interceptor.url as string)) { // && !byPassUrl.has(interceptor.url as string)) {
        removePending(interceptor); // check and cancel the previous IRequest before the IRequest starts
        addPending(interceptor); // add the current IRequest to pending
      }
      delete interceptor?.headers?.common?.fuName;
      return interceptor;
    }, (error) => {
      return Promise.reject(error);
    });

    this.instance.interceptors.response.use((response) => {
      const { method, url }: any = response.config;
      if (['get', 'post'].includes(method) && !byPassUrl.has(url)) {
        removePending(response); // after the IRequest, remove the IRequest
      }
      return response;
    }, (error) => {
      if (axios.isCancel(error)) {
        console.log(`Canceled repeated IRequest: ${error.message}`);
      } else {
        // handle error code
      }
      return Promise.reject(error);
    });
  }

  setHeader(key: string, value: string | undefined) {
    this.instance.defaults.headers.common[key] = value || '';
  }

  deleteHeader(key: string) {
    delete this.instance.defaults.headers.common[key];
  }

  getHeader(key?: string) {
    if (key) {
      return this.instance.defaults.headers.common[key];
    }

    return this.instance.defaults.headers.common;
  }

  makePostRequest(requestObject: IRequest) {
    return this.apiRequest(requestObject, 'post');
  }

  makePutRequest(requestObject: IRequest) {
    return this.apiRequest(requestObject, 'put');
  }

  makePatchRequest(requestObject: IRequest) {
    return this.apiRequest(requestObject, 'patch');
  }

  makeDeleteRequest(requestObject: IRequest) {
    return this.apiRequest(requestObject, 'delete');
  }

  makeGetRequest(requestObject: IRequest) {
    return this.apiRequest(requestObject, 'get');
  }

  private async apiRequest(requestObject: IRequest, methodType: Method) {
    return this.makeRequest(requestObject, methodType);
  }

  // private async apiRequest(requestObject: IRequest, methodType: Method) {
  //   try {
  //     return await this.makeRequest(requestObject, methodType);
  //   } catch (error) {
  //     const err: any = error;
  //     console.log('error', err);
  //     return {
  //       error: get(err, 'response.data.errors', err.message),
  //       status: get(err, 'response.status'),
  //     };
  //   }
  // }

  private async makeRequest(
    { url, data, config }: IRequest,
    type: Method = 'get',
    activeRetryCount?: number,
  ): Promise<any> {
    let tokenPollingFlag = false;

    const fetchReturnPromise = (resolve: any, reject: any) => {
      let retryCount = Number(activeRetryCount) || 0;
      if (retryCount > this.retries) {
        throw Error(`Unable to make the request (total retry: ${retryCount})`);
      }
      const payload: any = [url];
      switch (type) {
        case 'get':
        case 'delete':
          payload.push(config);
          break;
        case 'post':
        case 'put':
        case 'patch':
          payload.push(data);
          payload.push(config);
          break;
      }
      this.instance[type].apply(null, payload)
        .then((response: any) => {
          if (url === refreshTokenApiUrl)
            tokenPromise = null;

          if (url !== refreshTokenApiUrl && url !== loginApiUrl && response.data.code == 401)
            throw response;

          resolve(response);
        }).catch(async (error: any) => {
          if (!tokenPollingFlag && (error?.response?.status === 401 || error?.data?.code == 401)) {
            tokenPollingFlag = true;
            // const token = await storageService.get(storageService.ACCESS_TOKEN);
            const refreshToken = await storageService.get(storageService.REFRESH_TOKEN);
            try {
              axiosInstance.setHeader(headerKey.Authorization, '');
              const { data } = await axiosInstance.makePostRequest({
                url: refreshTokenApiUrl,
                data: { refreshToken },
                config: { headers: { portal: 'CLIENT' } },
              });
              storageService.set(storageService.ACCESS_TOKEN, data.accessToken);
              // axiosInstance.setHeader(headerKey.AccessToken, data.accessToken);
              // axiosInstance.setHeader(headerKey.Authorization, `${data.tokenType} ${data.accessToken}`);
              axiosInstance.setHeader(headerKey.Authorization, `${data.tokenType} ${data.accessToken}`);
            } catch (error) {
              console.log('error', error);
              return reject(error);
            }
            return fetchReturnPromise(resolve, reject);
          } else {
            retryCount++;
            // await delay(1000 * this.retriesDelay ** retryCount);
            // return await this.makeRequest({ url, data }, type, retryCount);
            // if (!error)
            reject(error);
            // reject({ ...error, message: error.message || error.message });
          }
          tokenPollingFlag = false;
        });
    };

    if (tokenPromise && url === refreshTokenApiUrl)
      return tokenPromise;
    const fetchPromise = new Promise(fetchReturnPromise);

    if (url === refreshTokenApiUrl)
      tokenPromise = fetchPromise;
    return fetchPromise;
  }
}

export const headerKey = {
  AccessToken: 'access-token',
  AuthToken: 'AUTH-TOKEN',
  Authorization: 'authorization',
  Admin: 'admin',
  AdminID: 'adminId',
  ContentType: 'Content-Type',
};

const baseURL = process.env.REACT_APP_BASE_URL || '';
export const axiosInstance = new AxiosClient({ baseURL, retries: 3, headers: {} });
